import { useRef } from 'react'
import { History } from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material'
import Grid2 from '@mui/material/Grid2'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useGetOrgCredentialType } from '@/service-library/hooks/org-credential-types'
import { EntrataVendor } from '@/types/entrata-vendor'
import { LiveListEntity } from '@/types/live-lists'
import { BaseVendor } from '@/types/relay'
import FlagsBulletList from '@/components/flags/FlagsBulletList'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import CredentialTypeProvider from '@/components/integration-settings/CredentialTypeProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import VendorCardFieldValue from '@/components/vendor-card/FieldValue'
import VendorSettings from '@/components/vendor-card/VendorSettings'
import EntrataNeedsReviewSection from './EntrataNeedsReviewSection'
import EntrataLiveVendorList from './fields/EntrataLiveVendorList'
import { liveListNoneOption } from './fields/LiveListPickerField'
import FocusContainer from './FocusContainer'
import { useProjectGridFieldsContext } from './ProjectGridFieldsProvider'
import { useSelectedFieldContext } from './providers/SelectedFieldProvider'
import useFieldValue from './useFieldValue'

type EntrataVendorCardProps = {
  orgId: string
}

const minimalVendorValuesMap: [string, keyof EntrataVendor][] = [
  ['Vendor Name', 'display_name'],
  ['Vendor Location', 'location_name'],
  ['Vendor Account', 'vendor_account'],
  ['Default GL Account Number', 'default_gl_account_number'],
  ['Default GL Account Name', 'default_gl_account_name'],
  ['Default Routing Tag', 'default_routing_tag'],
  ['Address Line 1', 'address_line_1'],
  ['City', 'city'],
  ['State', 'state'],
  ['Postal Code', 'postal_code'],
  ['Country', 'country'],
]

const vendorValuesMap: [string, keyof EntrataVendor][] = [
  ['Vendor Name', 'display_name'],
  ['Vendor ID', 'vendor_id'],
  ['Vendor Location', 'location_name'],
  ['Vendor Location ID', 'location_id'],
  ['Vendor Account', 'vendor_account'],
  ['Vendor Account Number', 'vendor_account_number'],
  ['Vendor Account Remittance ID', 'vendor_account_remittance_id'],
  ['Default GL Account Number', 'default_gl_account_number'],
  ['Default GL Account Name', 'default_gl_account_name'],
  ['Default Routing Tag ID', 'default_routing_tag_id'],
  ['Default Routing Tag', 'default_routing_tag'],
  ['Status', 'vendor_status'],
  ['Capture Invoice Total Only', 'is_capture_invoice_total_only'],
  ['Require PO for Invoice', 'require_po_for_invoice'],
  ['Payment Term ID', 'payment_term_id'],
  ['Payment Term', 'payment_term'],
  ['Default Remittance ID', 'default_remittance_id'],
  ['Default Remittance', 'default_remittance'],
  ['Default Remittance Type', 'default_remittance_type'],
  ['Address Line 1', 'address_line_1'],
  ['Address Line 2', 'address_line_2'],
  ['City', 'city'],
  ['State', 'state'],
  ['Postal Code', 'postal_code'],
  ['Country', 'country'],
]

export default function EntrataVendorCard({ orgId }: EntrataVendorCardProps) {
  const [minimalDetails, setMinimalDetails] = useLocalStorage(
    'entrata-vendor-view-show-more',
    true,
  )
  const [showSettings, setShowSettings] = useLocalStorage(
    'entrata-vendor-card-show-settings',
    false,
  )

  const { project } = useProjectContext()
  const { orgCredentialType } = useGetOrgCredentialType({
    id: project.org_credential_type_id || '',
  })

  const fields = useProjectGridFieldsContext()
  const { isDisabled } = useDocumentContext()
  const { setSelectedField, isSelectedField } = useSelectedFieldContext()

  const focusContainerRef = useRef<HTMLDivElement>(null)

  const invoiceVendorField = fields.find(
    (field) => field.name === 'Invoice Vendor',
  )
  const {
    value,
    dataLineageValues,
    documentRowValue,
    jsonValue,
    hasManualValue,
    metadata,
    onManualChange,
  } = useFieldValue({
    field: invoiceVendorField,
  })

  const rowValueFlags = documentRowValue?.row_value_flags || []

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <div style={{ flexGrow: 1 }}>
          <FocusContainer
            focusContainerRef={focusContainerRef}
            isSelectedField={isSelectedField(
              invoiceVendorField?.id || null,
              documentRowValue?.document_row_id || null,
            )}
            isUserEditable={
              invoiceVendorField?.input_behavior !== 'manual_not_allowed'
            }
            onFocus={() => {
              !isDisabled &&
                invoiceVendorField &&
                setSelectedField({
                  document_row_id: documentRowValue?.document_row_id as string,
                  field: invoiceVendorField,
                })
            }}
            onBlur={() => {
              setSelectedField(null)
            }}
          >
            <EntrataLiveVendorList
              jsonValue={
                (value === '' && dataLineageValues.final_value === ''
                  ? liveListNoneOption
                  : jsonValue) as LiveListEntity<EntrataVendor>
              }
              onManualChange={onManualChange}
              openOnFocus
            />
          </FocusContainer>
        </div>
        {hasManualValue && invoiceVendorField && (
          <Box sx={{ flexShrink: 0, py: 0.5 }}>
            <Tooltip
              title={
                invoiceVendorField.input_behavior === 'manual_only'
                  ? 'Clear'
                  : 'Revert to System Results'
              }
            >
              <IconButton
                size="small"
                tabIndex={-1}
                onClick={() => {
                  onManualChange({ manualValue: null })
                }}
              >
                <History fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Stack>
      <EntrataNeedsReviewSection
        cardType="vendor"
        columnId={jsonValue?.entity.vendor_id}
        orgId={orgId}
      />
      <Grid2 container>
        {(minimalDetails ? minimalVendorValuesMap : vendorValuesMap).map(
          ([label, key]) => {
            let displayedValue = jsonValue?.entity[key]
            if (typeof displayedValue === 'boolean') {
              displayedValue = displayedValue ? 'True' : 'False'
            }
            return (
              <VendorCardFieldValue
                key={key}
                field={label}
                value={
                  invoiceVendorField ? displayedValue : <Skeleton width={100} />
                }
              />
            )
          },
        )}
        {!minimalDetails && (
          <>
            {(metadata.matched_field || metadata.matched_by_alias) && (
              <>
                <Grid2 size={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid2>
                <VendorCardFieldValue
                  field="Matched Field"
                  value={metadata.matched_field || ''}
                />
                <VendorCardFieldValue
                  field="Matched Address"
                  value={metadata.matched_address || ''}
                />
                <VendorCardFieldValue
                  field="Matched By Account Number"
                  value={metadata.matched_by_account_number ? 'True' : 'False'}
                />
                <VendorCardFieldValue
                  field="Matched By Remittance"
                  value={
                    metadata.matched_by_remittance_address ? 'True' : 'False'
                  }
                />
                <VendorCardFieldValue
                  field="Matched By Alias"
                  value={metadata.matched_by_alias ? 'True' : 'False'}
                />
              </>
            )}
          </>
        )}
      </Grid2>
      <Box>
        <Button
          variant="text"
          onClick={() => setMinimalDetails((prev) => !prev)}
          sx={{ fontSize: 12, ml: '-5px' }}
        >
          Show {minimalDetails ? 'More' : 'Less'}
        </Button>
      </Box>

      {orgCredentialType && orgId && jsonValue?.entity_id && (
        <>
          {showSettings && (
            <CredentialTypeProvider credentialType={orgCredentialType}>
              <VendorSettings
                orgId={orgId}
                vendor={jsonValue as LiveListEntity<BaseVendor>}
              />
            </CredentialTypeProvider>
          )}
          <Box>
            <Button
              variant="text"
              onClick={() => setShowSettings((prev) => !prev)}
              sx={{ fontSize: 12, ml: '-5px' }}
            >
              {showSettings ? 'Hide Settings' : 'Show Settings'}
            </Button>
          </Box>
        </>
      )}

      {rowValueFlags.length > 0 && (
        <FlagsBulletList rowValueFlags={rowValueFlags} noPadding />
      )}
    </Stack>
  )
}
